import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin, CredentialResponse } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";

import jmcLogo from "../../assets/jmc-logo.png";
import "../../App.css";

type GooglePayload = {
  email?: string;
  name?: string;
};

type AllowlistResponse = {
  allowed: boolean;
  reason?: string;
  staff_name?: string;
  role?: string;
  department?: string;
};

export default function StaffLanding() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
  const guardError = localStorage.getItem("staff_guard_error") || "";
  if (guardError) {
    setErrorMsg(guardError);
    localStorage.removeItem("staff_guard_error");
  }
}, []);



  const showJapaneseBlockMessage = (reason?: string) => {
  if (reason === "email_not_found") {
    setErrorMsg("このGoogleアカウントは許可されていません。管理者に連絡してください。");
    return;
  }
  if (reason === "not_enabled_or_not_admin") {
    setErrorMsg("このアカウントは無効、または管理者権限がありません。管理者に連絡してください。");
    return;
  }
  if (reason === "allowlist_empty") {
    setErrorMsg("許可リストが未設定です。管理者に連絡してください。");
    return;
  }
  setErrorMsg("ログイン制限チェックに失敗しました。管理者に連絡してください。");
};


  const handleSuccess = async (cred: CredentialResponse) => {
    if (!cred.credential) {
      alert("Googleログインの認証情報が取得できませんでした。");
      return;
    }

    const decoded = jwtDecode<GooglePayload>(cred.credential);
    const email = (decoded.email || "").trim().toLowerCase();
    const name = decoded.name || "";

    if (!email) {
      alert("Googleアカウントのメールアドレスが取得できませんでした。");
      return;
    }

    try {
      setErrorMsg("");

      setChecking(true);

      // Call backend allowlist check (secure)
      const res = await fetch("/api/staff/check-allowlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        showJapaneseBlockMessage();
        return;
      }

      const data: AllowlistResponse = await res.json();

      if (!data.allowed) {
        showJapaneseBlockMessage(data.reason);
        return;
      }

      // Allowed => store staff info and proceed
      localStorage.setItem("staff_email", email);
      localStorage.setItem("staff_name", name);
      localStorage.setItem("staff_google_token", cred.credential);

      // optional extra info (safe)
      if (data.department) localStorage.setItem("staff_department", data.department);
      if (data.role) localStorage.setItem("staff_role", data.role);

      navigate("/staff/dashboard");
    } catch (e) {
      showJapaneseBlockMessage();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="ml-page">
      <header className="ml-topbar">
        <div
          className="ml-left"
          onClick={() => navigate("/")}
          role="button"
          tabIndex={0}
        >
          <img src={jmcLogo} alt="JMC" className="ml-logo" />
          <div>
            <div className="ml-brand-name">JMC</div>
            <div className="ml-brand-sub">スタッフ管理ポータル</div>
          </div>
        </div>

        <button className="ml-back" onClick={() => navigate("/")}>
          戻る
        </button>
      </header>

      <main className="ml-main">
        <div className="ml-card">
          <div className="ml-card-header">
            <div className="ml-card-title">スタッフ用ポータル</div>
            <div className="ml-card-sub" style={{ color: "#334155" }}>
              この画面はJMCスタッフ専用です。加盟店アカウント発行・進捗管理・書類確認などを行います。
            </div>
          </div>

          <div
            className="ml-form"
            style={{ background: "#ffffff", border: "1px solid #e2e8f0" }}
          >
            <div
              style={{
                marginBottom: 12,
                color: "#334155",
                fontSize: 13,
                lineHeight: 1.7,
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: 6 }}>主な作業</div>
              ・加盟店の新規登録（ID/PW発行）<br />
              ・基本情報の入力（会社名・担当者情報など）<br />
              ・提出状況（入力/アップロード）の進捗管理<br />
              ・書類の確認／差戻し（今後実装）
            </div>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "12px 0" }}>
  {checking ? (
    <div style={{ color: "#334155", fontSize: 13 }}>
      確認中...
    </div>
  ) : (
    <GoogleLogin
      onSuccess={handleSuccess}
      onError={() => setErrorMsg("Googleログインに失敗しました")}
      useOneTap={false}
    />
  )}

  {errorMsg && (
    <div
      style={{
        marginTop: 12,
        width: "100%",
        background: "#FEF2F2",
        border: "1px solid #FCA5A5",
        color: "#991B1B",
        padding: "10px 12px",
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.6,
      }}
    >
      {errorMsg}
    </div>
  )}
</div>

          </div>

          <div className="ml-help" style={{ color: "#334155" }}>
            ※ 会社が許可したGoogleアカウントのみログイン可能です
          </div>
        </div>
      </main>
    </div>
  );
}
